import React, { useState, lazy } from 'react';
import { Check, Lock, Zap, ShieldCheck, CreditCard } from 'lucide-react';
interface PricingCardsProps {
  id: string;
}
const PRO_MOCKUP = 'https://cdn.magicpatterns.com/uploads/ckqiv7a44ZPWd8Gpdx3tho/Megapack_guardia_psiquiatrica_PRO.png';
const ELITE_MOCKUP = 'https://cdn.magicpatterns.com/uploads/1g22UE8gazCg9rSDG2swnC/Megapack_guardia_psiquiatrica_PRO_ELITE.png';
export function PricingCards({
  id
}: PricingCardsProps) {
  const [selectedOption, setSelectedOption] = useState<'elite' | 'pro'>('elite');
  const eliteLink = 'https://checkout.mundodigitalarg.com/cart/48243576111345:1';
  const proLink = 'https://checkout.mundodigitalarg.com/cart/48243576045809:1';
  const currentLink = selectedOption === 'elite' ? eliteLink : proLink;
  const currentPrice = selectedOption === 'elite' ? '$35.990' : '$24.990';
  return <section id={id} className="py-6 md:py-16 bg-brand-bg scroll-mt-12" data-id="element-188">
      <div className="max-w-2xl mx-auto px-4" data-id="element-189">
        <div className="text-center mb-4 md:mb-8" data-id="element-190">
          <h2 className="font-heading text-[22px] md:text-4xl text-brand-dark mb-1" data-id="element-191">
            Elegí tu Megapack
          </h2>
          <p className="text-gray-600 text-xs md:text-base font-medium" data-id="element-192">
            Ambas opciones incluyen los 6 bonos clínicos
          </p>
        </div>

        <div className="flex flex-col gap-3 md:gap-4 mb-4 md:mb-6" data-id="element-193">
          {/* PRO Base Card — compact horizontal */}
          <div onClick={() => setSelectedOption('pro')} className={`bg-white rounded-xl border-2 cursor-pointer transition-all duration-300 active:scale-[0.99] overflow-hidden ${selectedOption === 'pro' ? 'border-brand-mid shadow-md' : 'border-blue-100 hover:border-blue-300'}`} data-id="element-194">
            <div className="flex items-stretch" data-id="element-195">
              {/* Mockup left — 1/3 width */}
              <div className="w-24 md:w-32 shrink-0 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-2 md:p-3 relative" data-id="element-196">
                <img src={PRO_MOCKUP} alt="Mockup Megapack PRO" loading="lazy" className="max-h-full max-w-full object-contain drop-shadow-md" data-id="element-197" />
              </div>

              {/* Content right */}
              <div className="flex-1 p-3 md:p-4 min-w-0" data-id="element-198">
                <div className="flex items-start justify-between mb-1.5 md:mb-2 gap-2" data-id="element-199">
                  <div className="min-w-0" data-id="element-200">
                    <h3 className="font-bold text-gray-900 text-sm md:text-base leading-tight" data-id="element-201">
                      Megapack PRO
                    </h3>
                    <p className="text-[10px] md:text-xs text-gray-500" data-id="element-202">
                      6 Tomos + 6 Bonos
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedOption === 'pro' ? 'border-brand-mid' : 'border-gray-300'}`} data-id="element-203">
                    {selectedOption === 'pro' && <div className="w-2.5 h-2.5 bg-brand-mid rounded-full" data-id="element-204"></div>}
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mb-2" data-id="element-205">
                  <span className="text-xl md:text-2xl font-heading text-gray-900" data-id="element-206">
                    $24.990
                  </span>
                  <span className="text-gray-400 line-through text-[11px] md:text-xs" data-id="element-207">
                    $59.990
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 md:gap-2" data-id="element-208">
                  {['6 Tomos', '6 Bonos', 'Acceso inmediato', 'Garantía 7d'].map((item, i) => <span key={i} className="inline-flex items-center gap-1 text-[10px] md:text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-209">
                      <Check className="w-2.5 h-2.5 text-brand-mid" data-id="element-210" /> {item}
                    </span>)}
                </div>
              </div>
            </div>
          </div>

          {/* ELITE Card — compact horizontal, highlighted */}
          <div onClick={() => setSelectedOption('elite')} className={`bg-brand-dark rounded-xl border-2 cursor-pointer transition-all duration-300 active:scale-[0.99] overflow-hidden relative ${selectedOption === 'elite' ? 'border-brand-gold shadow-[0_0_25px_rgba(212,175,55,0.35)]' : 'border-transparent hover:border-brand-mid'}`} data-id="element-211">
            <div className="absolute top-0 right-0 bg-brand-gold text-brand-dark text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-bl-lg uppercase tracking-wider whitespace-nowrap flex items-center gap-1 shadow-sm z-10" data-id="element-212">
              <span data-id="element-213">⭐</span> MÁS ELEGIDA
            </div>

            <div className="flex items-stretch" data-id="element-214">
              {/* Mockup left — 1/3 width */}
              <div className="w-24 md:w-32 shrink-0 bg-gradient-to-br from-brand-mid/30 to-brand-dark flex items-center justify-center p-2 md:p-3 relative" data-id="element-215">
                <img src={ELITE_MOCKUP} alt="Mockup Megapack Elite" loading="lazy" className="max-h-full max-w-full object-contain drop-shadow-lg" data-id="element-216" />
              </div>

              {/* Content right */}
              <div className="flex-1 p-3 md:p-4 pt-5 md:pt-6 min-w-0" data-id="element-217">
                <div className="flex items-start justify-between mb-1.5 md:mb-2 gap-2" data-id="element-218">
                  <div className="min-w-0" data-id="element-219">
                    <h3 className="font-bold text-white text-sm md:text-base leading-tight" data-id="element-220">
                      Megapack PRO Elite
                    </h3>
                    <p className="text-[10px] md:text-xs text-blue-200" data-id="element-221">
                      + Blindaje Legal Total
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedOption === 'elite' ? 'border-brand-gold' : 'border-gray-500'}`} data-id="element-222">
                    {selectedOption === 'elite' && <div className="w-2.5 h-2.5 bg-brand-gold rounded-full" data-id="element-223"></div>}
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mb-2" data-id="element-224">
                  <span className="text-xl md:text-2xl font-heading text-white" data-id="element-225">
                    $35.990
                  </span>
                  <span className="text-gray-400 line-through text-[11px] md:text-xs" data-id="element-226">
                    $79.990
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 md:gap-2" data-id="element-227">
                  {['Todo PRO', 'Blindaje Legal', 'Descargo sumario', 'Auditoría'].map((item, i) => <span key={i} className="inline-flex items-center gap-1 text-[10px] md:text-xs text-gray-200 bg-white/10 px-1.5 py-0.5 rounded" data-id="element-228">
                      <Check className="w-2.5 h-2.5 text-brand-gold" data-id="element-229" /> {item}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shared CTA Button */}
        <div className="text-center" data-id="element-230">
          <a href={currentLink} className="block w-full bg-brand-cta hover:bg-orange-500 active:scale-[0.98] text-white font-bold text-base md:text-xl py-3.5 md:py-4 px-5 md:px-6 rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all animate-pulse-slow mb-2 min-h-[52px]" data-id="element-231">
            💥 COMPRAR {selectedOption === 'elite' ? 'ELITE' : 'PRO'} ·{' '}
            {currentPrice}
          </a>
          <p className="text-[10px] md:text-xs text-gray-500 mb-3 md:mb-4" data-id="element-232">
            Elegiste:{' '}
            <span className="font-bold text-brand-dark" data-id="element-233">
              {selectedOption === 'elite' ? 'Megapack PRO Elite' : 'Megapack PRO'}
            </span>
          </p>

          <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 md:gap-5 text-[10px] md:text-xs text-gray-500 font-medium" data-id="element-234">
            <div className="flex items-center gap-1" data-id="element-235">
              <Lock className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-236" /> Pago seguro
            </div>
            <div className="flex items-center gap-1" data-id="element-237">
              <Zap className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-238" /> Acceso 5 min
            </div>
            <div className="flex items-center gap-1" data-id="element-239">
              <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-600" data-id="element-240" />{' '}
              Garantía 7 días
            </div>
            <div className="flex items-center gap-1" data-id="element-241">
              <CreditCard className="w-3 h-3 md:w-3.5 md:h-3.5 text-blue-500" data-id="element-242" />{' '}
              Mercado Pago
            </div>
          </div>
        </div>
      </div>
    </section>;
}